<template>
    <VJoyWrapper
        ref="itemRef"
        class="task-list-item"
        :class="{'task-list-item--completed': task.completed}"
        wrap="nowrap"
        justify="space-between"
        align="center"
        column-gap="2"
        no-margin
        @click="task.completed ? undefined : task.onClick()"
    >
        <VJoyIcon :name="task.icon" size="xsmall" :color="task.completed ? 'neutral' : 'secondary'" />
        <VJoyWrapper direction="column" justify="space-between" align="flex-start" row-gap="1" no-margin>
            <VJoyTooltip :text="task.title" :delay="350">
                <span class="task-list-item__title">{{ task.title }}</span>
            </VJoyTooltip>
            <VJoyTooltip :text="task.description" :delay="350">
                <span class="task-list-item__description">{{ task.description }}</span>
            </VJoyTooltip>
        </VJoyWrapper>
        <VJoyIcon v-if="task.completed" name="check-circle" color="success" size="small" />
        <VJoyIcon v-else name="chevron-right" size="xsmall" />
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {watch, useTemplateRef, type ComponentPublicInstance, onMounted} from 'vue';
    import {launchConfetti} from '@malt/vue-animation';
    import {VJoyWrapper, VJoyIcon, VJoyTooltip} from '@maltjoy/core-vue';
    import type {TaskListItemType} from '../types/TaskListItemType';

    const props = defineProps<{task: TaskListItemType}>();
    const emit = defineEmits<{'confetti-animation-ended': []}>();

    // ##### Confetti
    watch(
        () => props.task,
        async () => {
            await triggerConfettisIfNeeded();
        },
    );
    onMounted(async () => {
        await triggerConfettisIfNeeded();
    });

    const itemRef = useTemplateRef<ComponentPublicInstance>('itemRef');

    async function triggerConfettisIfNeeded() {
        const taskListItemsNeedsConfettis = JSON.parse(localStorage.getItem('task-list-items-needs-confettis') ?? '[]');
        if (!taskListItemsNeedsConfettis) {
            return;
        }
        const needConfettis = taskListItemsNeedsConfettis.includes(props.task.id);
        if (!needConfettis || !props.task.completed) {
            return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Otherwise it scrolls too early on page load, i don't know why

        itemRef.value?.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (!itemRef.value?.$el) {
            return;
        }
        launchConfetti(itemRef.value.$el, {
            dataTestid: 'task-list-item-confettis',
            confettiOptions: {
                particleCount: 300,
                spread: 80,
                origin: {y: 2.5},
                startVelocity: 30,
            },
        });
        setTimeout(() => {
            emit('confetti-animation-ended');
        }, 2200);
        localStorage.setItem(
            'task-list-items-needs-confettis',
            JSON.stringify([...taskListItemsNeedsConfettis.filter((id: string) => id !== props.task.id)]),
        );
    }
</script>

<style scoped lang="scss">
    .task-list-item {
        padding: var(--joy-core-spacing-4) var(--joy-core-spacing-6);
        border: 2px solid var(--joy-color-neutral-30);
        border-radius: var(--joy-core-radius-4);
        min-width: 280px;
        height: 90px;
        cursor: pointer;

        &--completed {
            background-color: var(--joy-color-neutral-20);
            cursor: default;
        }

        &:hover:not(&--completed) {
            background-color: var(--joy-color-neutral-10);
        }

        &__title {
            font-size: var(--joy-font-size-primary-300);
            font-weight: var(--joy-font-weight-bold);
            color: var(--joy-color-neutral-60);
            display: -webkit-box;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: pre-wrap;
            line-clamp: 1;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
        }

        &__description {
            font-size: var(--joy-font-size-primary-300);
            color: var(--joy-color-text-default);
            display: -webkit-box;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: pre-wrap;
            line-clamp: 2;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }
    }
</style>
